import React from 'react';
import { jsPDF } from 'jspdf';

const DEFAULT_FILE_BASENAME = 'grilla';
const DEFAULT_SHEET_NAME = 'Datos';
const CELL_PADDING = 6;
const BODY_FONT_SIZE = 9;
const HEADER_FONT_SIZE = 9;
const LINE_HEIGHT = 11;
const textEncoder = new TextEncoder();

const normalizeHeader = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const escapeHtml = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeFileName = (value = DEFAULT_FILE_BASENAME) => {
  const normalized = String(value || DEFAULT_FILE_BASENAME)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

  return normalized || DEFAULT_FILE_BASENAME;
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatPrimitiveValue = (value) => {
  if (value == null || typeof value === 'boolean') return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toLocaleString('es-AR');
  }

  if (Array.isArray(value)) {
    return value.map(formatPrimitiveValue).filter(Boolean).join(', ');
  }

  if (typeof value === 'object') {
    const candidateKeys = ['label', 'nombre', 'name', 'title', 'descripcion', 'description', 'codigo', 'code'];
    const matchingKey = candidateKeys.find((key) => value[key]);
    if (matchingKey) {
      return formatPrimitiveValue(value[matchingKey]);
    }
    return '';
  }

  return String(value);
};

const extractTextFromNode = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);

  if (Array.isArray(node)) {
    return node.map(extractTextFromNode).filter(Boolean).join(' ');
  }

  if (React.isValidElement(node)) {
    return extractTextFromNode(node.props?.children);
  }

  if (typeof node === 'object') {
    return '';
  }

  return String(node);
};

const getColumnTitle = (column) => column?.title || column?.label || column?.key || '';

export const isActionsColumn = (columnOrHeader) => {
  if (!columnOrHeader) return false;

  if (typeof columnOrHeader === 'string') {
    const normalized = normalizeHeader(columnOrHeader);
    return normalized === 'acciones' || normalized === 'actions';
  }

  const key = normalizeHeader(columnOrHeader.key || '');
  const title = normalizeHeader(getColumnTitle(columnOrHeader));
  return key === 'actions' || title === 'acciones' || title === 'actions';
};

const buildMatrixFromColumns = ({ columns = [], rows = [] }) => {
  const exportableColumns = columns.filter((column) => !isActionsColumn(column));
  const headers = exportableColumns.map((column) => getColumnTitle(column));

  const matrixRows = rows.map((item, index) =>
    exportableColumns.map((column) => {
      const rawValue = item?.[column.key];

      if (typeof column.exportValue === 'function') {
        return formatPrimitiveValue(column.exportValue(rawValue, item, index));
      }

      if (typeof column.render === 'function') {
        const renderedText = extractTextFromNode(column.render(rawValue, item, index));
        if (renderedText.trim()) return renderedText.trim();
      }

      if (typeof column.formatter === 'function') {
        const formattedValue = column.formatter(rawValue, item, index);
        const formattedText = extractTextFromNode(formattedValue);
        if (formattedText.trim()) return formattedText.trim();
        return formatPrimitiveValue(formattedValue);
      }

      return formatPrimitiveValue(rawValue);
    })
  );

  return { headers, rows: matrixRows };
};

const extractMatrixFromTable = (tableElement) => {
  if (!tableElement) {
    return { headers: [], rows: [] };
  }

  const headerCells = Array.from(tableElement.querySelectorAll('thead th'));
  const candidateColumns = [];

  headerCells.forEach((cell, index) => {
    const text = cell.innerText?.trim() || '';
    if (isActionsColumn(text) || !text) return;
    candidateColumns.push({ index, header: text });
  });

  const bodyRows = Array.from(tableElement.querySelectorAll('tbody tr'));
  const normalizedBodyRows = bodyRows.map((row) => {
    const cells = Array.from(row.querySelectorAll('td, th'));
    return candidateColumns.map(({ index }) => {
      const cell = cells[index];
      if (!cell) return '';
      return (cell.innerText || '')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
    });
  });

  const includedColumnIndexes = candidateColumns
    .map((column, columnIndex) => ({
      ...column,
      hasContent: normalizedBodyRows.some((row) => String(row[columnIndex] || '').trim() !== '')
    }))
    .filter((column) => column.hasContent);

  const headers = includedColumnIndexes.map((column) => column.header);

  const rows = bodyRows
    .map((row) => {
      const cells = Array.from(row.querySelectorAll('td, th'));
      const values = includedColumnIndexes.map(({ index }) => {
        const cell = cells[index];
        if (!cell) return '';
        return (cell.innerText || '')
          .replace(/\u00a0/g, ' ')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n[ \t]+/g, '\n')
          .replace(/[ \t]{2,}/g, ' ')
          .trim();
      });

      return values;
    })
    .filter((row) => row.some((value) => String(value || '').trim() !== ''));

  return { headers, rows };
};

const createUint8Array = (...bytes) => Uint8Array.from(bytes);

const uint16ToBytes = (value) => createUint8Array(
  value & 0xff,
  (value >> 8) & 0xff
);

const uint32ToBytes = (value) => createUint8Array(
  value & 0xff,
  (value >> 8) & 0xff,
  (value >> 16) & 0xff,
  (value >> 24) & 0xff
);

const concatUint8Arrays = (arrays) => {
  const totalLength = arrays.reduce((sum, current) => sum + current.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;

  arrays.forEach((array) => {
    result.set(array, offset);
    offset += array.length;
  });

  return result;
};

const encodeUtf8 = (value) => textEncoder.encode(String(value ?? ''));

const sanitizeSheetName = (value = DEFAULT_SHEET_NAME) => {
  const sanitized = String(value || DEFAULT_SHEET_NAME)
    .replace(/[\\/*?:[\]]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 31);

  return sanitized || DEFAULT_SHEET_NAME;
};

const getColumnLetter = (index) => {
  let value = index + 1;
  let result = '';

  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }

  return result;
};

const getDosDateTime = (date = new Date()) => {
  const safeYear = Math.max(date.getFullYear(), 1980);
  const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((safeYear - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { dosTime, dosDate };
};

const createCrc32Table = () => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let current = index;
    for (let iteration = 0; iteration < 8; iteration += 1) {
      current = (current & 1) ? (0xedb88320 ^ (current >>> 1)) : (current >>> 1);
    }
    table[index] = current >>> 0;
  }

  return table;
};

const crc32Table = createCrc32Table();

const calculateCrc32 = (bytes) => {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc = crc32Table[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const buildZipBlob = (files) => {
  const localParts = [];
  const centralParts = [];
  let offset = 0;
  const { dosTime, dosDate } = getDosDateTime();

  files.forEach((file) => {
    const fileNameBytes = encodeUtf8(file.name);
    const contentBytes = typeof file.content === 'string' ? encodeUtf8(file.content) : file.content;
    const crc = calculateCrc32(contentBytes);

    const localHeader = concatUint8Arrays([
      uint32ToBytes(0x04034b50),
      uint16ToBytes(20),
      uint16ToBytes(0),
      uint16ToBytes(0),
      uint16ToBytes(dosTime),
      uint16ToBytes(dosDate),
      uint32ToBytes(crc),
      uint32ToBytes(contentBytes.length),
      uint32ToBytes(contentBytes.length),
      uint16ToBytes(fileNameBytes.length),
      uint16ToBytes(0),
      fileNameBytes
    ]);

    localParts.push(localHeader, contentBytes);

    const centralHeader = concatUint8Arrays([
      uint32ToBytes(0x02014b50),
      uint16ToBytes(20),
      uint16ToBytes(20),
      uint16ToBytes(0),
      uint16ToBytes(0),
      uint16ToBytes(dosTime),
      uint16ToBytes(dosDate),
      uint32ToBytes(crc),
      uint32ToBytes(contentBytes.length),
      uint32ToBytes(contentBytes.length),
      uint16ToBytes(fileNameBytes.length),
      uint16ToBytes(0),
      uint16ToBytes(0),
      uint16ToBytes(0),
      uint16ToBytes(0),
      uint32ToBytes(0),
      uint32ToBytes(offset),
      fileNameBytes
    ]);

    centralParts.push(centralHeader);
    offset += localHeader.length + contentBytes.length;
  });

  const centralDirectory = concatUint8Arrays(centralParts);
  const localContent = concatUint8Arrays(localParts);
  const endOfCentralDirectory = concatUint8Arrays([
    uint32ToBytes(0x06054b50),
    uint16ToBytes(0),
    uint16ToBytes(0),
    uint16ToBytes(files.length),
    uint16ToBytes(files.length),
    uint32ToBytes(centralDirectory.length),
    uint32ToBytes(localContent.length),
    uint16ToBytes(0)
  ]);

  return new Blob(
    [concatUint8Arrays([localContent, centralDirectory, endOfCentralDirectory])],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
  );
};

const buildWorksheetXml = ({ title, headers, rows }) => {
  const bodyRows = rows.map((row) => row.map((cell) => String(cell ?? '')));
  const worksheetRows = [];

  if (title) {
    worksheetRows.push({
      values: [String(title)],
      styleIndex: 1,
      mergeAcross: Math.max(headers.length, 1)
    });
  }

  worksheetRows.push({
    values: headers.map((header) => String(header ?? '')),
    styleIndex: 1
  });

  bodyRows.forEach((row) => {
    worksheetRows.push({
      values: row,
      styleIndex: 0
    });
  });

  const maxColumnCount = Math.max(...worksheetRows.map((row) => row.values.length), 1);
  const lastColumnLetter = getColumnLetter(maxColumnCount - 1);
  const lastRowNumber = worksheetRows.length;

  const colsXml = Array.from({ length: maxColumnCount }, (_, columnIndex) => {
    const maxLength = Math.max(
      ...worksheetRows.map((row) => (row.values[columnIndex] || '').length),
      10
    );
    const width = Math.min(Math.max(maxLength * 1.15 + 2, 10), 48);
    return `<col min="${columnIndex + 1}" max="${columnIndex + 1}" width="${width.toFixed(2)}" customWidth="1"/>`;
  }).join('');

  const rowsXml = worksheetRows.map((row, rowIndex) => {
    const rowNumber = rowIndex + 1;
    const cellsXml = row.values.map((value, columnIndex) => {
      const reference = `${getColumnLetter(columnIndex)}${rowNumber}`;
      return `<c r="${reference}" t="inlineStr" s="${row.styleIndex}"><is><t xml:space="preserve">${escapeHtml(value)}</t></is></c>`;
    }).join('');

    return `<row r="${rowNumber}">${cellsXml}</row>`;
  }).join('');

  const mergeCellsXml = title && maxColumnCount > 1
    ? `<mergeCells count="1"><mergeCell ref="A1:${lastColumnLetter}1"/></mergeCells>`
    : '';

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:${lastColumnLetter}${lastRowNumber}" />
  <sheetViews>
    <sheetView workbookViewId="0" />
  </sheetViews>
  <sheetFormatPr defaultRowHeight="15" />
  <cols>${colsXml}</cols>
  <sheetData>${rowsXml}</sheetData>
  ${mergeCellsXml}
</worksheet>`;
};

const buildWorkbookXml = (sheetName) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${escapeHtml(sheetName)}" sheetId="1" r:id="rId1" />
  </sheets>
</workbook>`;

const buildWorkbookRelationshipsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml" />
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml" />
</Relationships>`;

const buildRootRelationshipsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml" />
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml" />
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml" />
</Relationships>`;

const buildContentTypesXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
  <Default Extension="xml" ContentType="application/xml" />
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml" />
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml" />
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml" />
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml" />
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml" />
</Types>`;

const buildStylesXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font>
      <sz val="11" />
      <name val="Calibri" />
    </font>
    <font>
      <b />
      <sz val="11" />
      <name val="Calibri" />
    </font>
  </fonts>
  <fills count="2">
    <fill><patternFill patternType="none" /></fill>
    <fill><patternFill patternType="gray125" /></fill>
  </fills>
  <borders count="1">
    <border><left /><right /><top /><bottom /><diagonal /></border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" />
  </cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" />
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" />
  </cellXfs>
  <cellStyles count="1">
    <cellStyle name="Normal" xfId="0" builtinId="0" />
  </cellStyles>
</styleSheet>`;

const buildAppXml = (sheetName) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>aluxIA</Application>
  <HeadingPairs>
    <vt:vector size="2" baseType="variant">
      <vt:variant><vt:lpstr>Worksheets</vt:lpstr></vt:variant>
      <vt:variant><vt:i4>1</vt:i4></vt:variant>
    </vt:vector>
  </HeadingPairs>
  <TitlesOfParts>
    <vt:vector size="1" baseType="lpstr">
      <vt:lpstr>${escapeHtml(sheetName)}</vt:lpstr>
    </vt:vector>
  </TitlesOfParts>
</Properties>`;

const buildCoreXml = () => {
  const now = new Date().toISOString();
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:creator>aluxIA</dc:creator>
  <cp:lastModifiedBy>aluxIA</cp:lastModifiedBy>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`;
};

const buildColumnWidths = (doc, headers, rows, usableWidth) => {
  doc.setFontSize(BODY_FONT_SIZE);
  const sampleRows = rows.slice(0, 50);
  const widths = headers.map((header, index) => {
    const headerWidth = doc.getTextWidth(String(header || '')) + CELL_PADDING * 2 + 10;
    const bodyWidth = sampleRows.reduce((maxWidth, row) => {
      const candidate = doc.getTextWidth(String(row[index] || '')) + CELL_PADDING * 2;
      return Math.max(maxWidth, candidate);
    }, 0);
    return Math.min(Math.max(headerWidth, bodyWidth, 70), 220);
  });

  const totalMeasuredWidth = widths.reduce((sum, width) => sum + width, 0);
  if (totalMeasuredWidth <= usableWidth || totalMeasuredWidth === 0) {
    return widths;
  }

  const scale = usableWidth / totalMeasuredWidth;
  return widths.map((width) => Math.max(55, width * scale));
};

const renderPdfHeader = (doc, headers, columnWidths, marginLeft, startY) => {
  let x = marginLeft;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(HEADER_FONT_SIZE);

  headers.forEach((header, index) => {
    const width = columnWidths[index];
    doc.setFillColor(241, 245, 249);
    doc.rect(x, startY, width, LINE_HEIGHT + CELL_PADDING * 2, 'FD');
    const textLines = doc.splitTextToSize(String(header || ''), width - CELL_PADDING * 2);
    doc.text(textLines, x + CELL_PADDING, startY + CELL_PADDING + HEADER_FONT_SIZE - 1);
    x += width;
  });

  return startY + LINE_HEIGHT + CELL_PADDING * 2;
};

const exportMatrixToPdf = ({ headers, rows, fileName, title }) => {
  const orientation = headers.length > 5 ? 'landscape' : 'portrait';
  const doc = new jsPDF({
    orientation,
    unit: 'pt',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;
  const marginY = 40;
  const usableWidth = pageWidth - marginX * 2;
  const columnWidths = buildColumnWidths(doc, headers, rows, usableWidth);

  let currentY = marginY;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title || 'Exportación de grilla', marginX, currentY);

  currentY += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, marginX, currentY);
  doc.setTextColor(0);
  currentY += 18;

  const renderHeader = () => {
    currentY = renderPdfHeader(doc, headers, columnWidths, marginX, currentY);
  };

  renderHeader();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(BODY_FONT_SIZE);

  rows.forEach((row) => {
    const cellLines = row.map((cell, index) => doc.splitTextToSize(String(cell || ''), columnWidths[index] - CELL_PADDING * 2));
    const rowHeight = Math.max(
      ...cellLines.map((lines) => Math.max(lines.length, 1) * LINE_HEIGHT + CELL_PADDING * 2),
      LINE_HEIGHT + CELL_PADDING * 2
    );

    if (currentY + rowHeight > pageHeight - marginY) {
      doc.addPage();
      currentY = marginY;
      renderHeader();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(BODY_FONT_SIZE);
    }

    let x = marginX;
    row.forEach((cell, index) => {
      const width = columnWidths[index];
      doc.rect(x, currentY, width, rowHeight);
      doc.text(cellLines[index], x + CELL_PADDING, currentY + CELL_PADDING + BODY_FONT_SIZE - 1);
      x += width;
    });

    currentY += rowHeight;
  });

  doc.save(`${sanitizeFileName(fileName)}.pdf`);
};

const exportMatrixToExcel = ({ headers, rows, fileName, title }) => {
  const sheetName = sanitizeSheetName(title || DEFAULT_SHEET_NAME);
  const blob = buildZipBlob([
    { name: '[Content_Types].xml', content: buildContentTypesXml() },
    { name: '_rels/.rels', content: buildRootRelationshipsXml() },
    { name: 'docProps/app.xml', content: buildAppXml(sheetName) },
    { name: 'docProps/core.xml', content: buildCoreXml() },
    { name: 'xl/workbook.xml', content: buildWorkbookXml(sheetName) },
    { name: 'xl/_rels/workbook.xml.rels', content: buildWorkbookRelationshipsXml() },
    { name: 'xl/styles.xml', content: buildStylesXml() },
    { name: 'xl/worksheets/sheet1.xml', content: buildWorksheetXml({ title, headers, rows }) }
  ]);
  downloadBlob(blob, `${sanitizeFileName(fileName)}.xlsx`);
};

const normalizeExportInput = (input) => {
  if (input?.tableElement) {
    return extractMatrixFromTable(input.tableElement);
  }

  return buildMatrixFromColumns({
    columns: input?.columns || [],
    rows: input?.rows || []
  });
};

export const exportTableToExcel = (input = {}) => {
  const { headers, rows } = normalizeExportInput(input);
  if (!headers.length || !rows.length) return false;

  exportMatrixToExcel({
    headers,
    rows,
    fileName: input.fileName || DEFAULT_FILE_BASENAME,
    title: input.title || 'Exportación de grilla'
  });

  return true;
};

export const exportTableToPdf = (input = {}) => {
  const { headers, rows } = normalizeExportInput(input);
  if (!headers.length || !rows.length) return false;

  exportMatrixToPdf({
    headers,
    rows,
    fileName: input.fileName || DEFAULT_FILE_BASENAME,
    title: input.title || 'Exportación de grilla'
  });

  return true;
};
